import { useRef, useState } from "react";
import { ClipboardPaste, ImagePlus, Loader2, UserRound, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";

async function compressAvatar(file: File): Promise<string> {
  const source = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error("图片读取失败"));
    reader.readAsDataURL(file);
  });
  const image = await new Promise<HTMLImageElement>((resolve, reject) => {
    const element = new Image();
    element.onload = () => resolve(element);
    element.onerror = () => reject(new Error("图片格式不受支持"));
    element.src = source;
  });
  const scale = Math.min(
    1,
    256 / Math.max(image.naturalWidth, image.naturalHeight),
  );
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("当前浏览器无法处理图片");
  context.drawImage(image, 0, 0, canvas.width, canvas.height);
  return canvas.toDataURL("image/webp", 0.82);
}

export function ContactAvatarInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (avatar: string) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files: File[]) => {
    const file = files.find((item) => item.type.startsWith("image/"));
    if (!file || busy) return;
    setBusy(true);
    try {
      onChange(await compressAvatar(file));
      toast.success("联系人头像已添加");
    } catch {
      toast.error("头像读取失败，请换一张图片");
    } finally {
      setBusy(false);
    }
  };

  const pasteFromClipboard = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const items = await navigator.clipboard.read();
      const imageItem = items.find((item) =>
        item.types.some((type) => type.startsWith("image/")),
      );
      if (!imageItem) {
        toast.error("剪贴板里没有图片，请先复制一张截图");
        return;
      }
      const type = imageItem.types.find((item) => item.startsWith("image/"))!;
      const blob = await imageItem.getType(type);
      onChange(
        await compressAvatar(new File([blob], "clipboard.png", { type })),
      );
      toast.success("联系人头像已粘贴");
    } catch {
      toast.error("无法读取剪贴板，请点头像手动选择图片");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-3 sm:col-span-2">
      <span className="text-xs font-medium text-muted-foreground">
        联系人头像
      </span>
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        aria-label="选择联系人头像文件"
        className="hidden"
        onChange={(event) => {
          void upload(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
      />
      <button
        type="button"
        aria-label={value ? "更换联系人头像" : "上传联系人头像"}
        title="点击选择图片；或复制截图后点右侧粘贴按钮"
        disabled={busy}
        onClick={() => fileInput.current?.click()}
        onPaste={(event) => {
          const files = Array.from(event.clipboardData.files).filter((file) =>
            file.type.startsWith("image/"),
          );
          if (files.length) {
            event.preventDefault();
            void upload(files);
          }
        }}
        className="relative grid size-12 shrink-0 place-items-center rounded-full border bg-muted/40 text-muted-foreground outline-none transition hover:border-foreground/30 focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
      >
        {busy ? (
          <Loader2 className="size-5 animate-spin" />
        ) : value ? (
          <img
            src={value}
            alt="联系人头像"
            className="size-full rounded-full object-cover"
          />
        ) : (
          <UserRound className="size-5" />
        )}
        <span className="absolute -right-0.5 -bottom-0.5 grid size-5 place-items-center rounded-full border bg-background text-foreground shadow-sm">
          <ImagePlus className="size-3" />
        </span>
      </button>
      {value && !busy && (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label="删除联系人头像"
          title="删除头像"
          onClick={() => onChange("")}
        >
          <X />
        </Button>
      )}
      <Button
        type="button"
        variant="ghost"
        size="icon-xs"
        aria-label="粘贴剪贴板头像"
        title="粘贴剪贴板头像（复制截图后直接点）"
        disabled={busy}
        onClick={() => void pasteFromClipboard()}
      >
        <ClipboardPaste />
      </Button>
    </div>
  );
}
